import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Tooltip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddCircle from "@mui/icons-material/AddCircle";
import TextFields from "@mui/icons-material/TextFields";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import toastError from "../../errors/toastError";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// CampaignsPhrase
// ---------------------------------------------------------------------------
const CampaignsPhrase = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(true);
  const [campaignflows, setCampaignFlows] = useState([]);
  const [ModalOpenPhrase, setModalOpenPhrase] = useState(false);
  const [campaignflowSelected, setCampaignFlowSelected] = useState();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  const getCampaigns = async () => {
    setLoading(true);
    try {
      const res = await api.get("/flowcampaign");
      setCampaignFlows(res.data.flow || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCampaigns();
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/flowcampaign/${campaignId}`);
      toast.success("Frase deletada");
      getCampaigns();
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingContact(null);
  };

  const onSaveModal = () => {
    getCampaigns();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <CampaignIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                Campanhas
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerenciamento de frases de campanha
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddCircle />}
            onClick={() => {
              setCampaignFlowSelected();
              setModalOpenPhrase(true);
            }}
            sx={{
              bgcolor: PRIMARY_MAIN,
              color: "#fff",
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              "&:hover": {
                bgcolor: PRIMARY_DARK,
                boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
              }
            }}
          >
            Campanha
          </Button>
        </Stack>

        {/* Conteúdo */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                Todas as campanhas
              </Typography>
              <Chip
                label={`${campaignflows.length} ${campaignflows.length !== 1 ? "campanhas" : "campanha"}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  bgcolor: "rgba(148, 163, 184, 0.08)",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "rgba(148, 163, 184, 0.2)"
                }}
              />
            </Stack>
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Lista de campanhas */}
          {!loading && (
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                {campaignflows.map((flow) => (
                  <Paper
                    key={flow.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: PRIMARY_MAIN,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                      }
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 2.5 }}
                    >
                      {/* Nome */}
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: `${PRIMARY_MAIN}10`,
                            color: PRIMARY_MAIN
                          }}
                        >
                          <TextFields sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                          {flow.name}
                        </Typography>
                      </Stack>

                      {/* Status */}
                      <Box sx={{ px: 3 }}>
                        <Chip
                          icon={flow.status ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                          label={flow.status ? "Ativo" : "Desativado"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: flow.status ? "rgba(16, 185, 129, 0.08)" : "rgba(148, 163, 184, 0.08)",
                            color: flow.status ? "#10b981" : "text.secondary",
                            border: "1px solid",
                            borderColor: flow.status ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)",
                            "& .MuiChip-icon": {
                              color: flow.status ? "#10b981" : "text.secondary"
                            }
                          }}
                        />
                      </Box>

                      {/* Ações */}
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCampaignFlowSelected(flow.id);
                              setModalOpenPhrase(true);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { 
                                color: PRIMARY_MAIN, 
                                bgcolor: `${PRIMARY_MAIN}10` 
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <Tooltip title="Deletar">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmModalOpen(true);
                                  setDeletingContact(flow);
                                }}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": { 
                                    color: "error.main", 
                                    bgcolor: "rgba(239, 68, 68, 0.08)" 
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}

                {/* Empty state */}
                {campaignflows.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <CampaignIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: "text.secondary", mb: 1 }}>
                      Nenhuma campanha criada
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                      Clique no botão "Campanha" para criar sua primeira campanha
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingContact.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <CampaignModalPhrase
        open={ModalOpenPhrase}
        onClose={() => setModalOpenPhrase(false)}
        FlowCampaignId={campaignflowSelected}
        onSave={onSaveModal}
      />
    </Box>
  );
};

export default CampaignsPhrase;